import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Plus, 
  Heart, 
  ChatCircle, 
  Code, 
  SignOut, 
  GearSix,
  MagnifyingGlass,
  House,
  User,
  ShieldCheck,
  Trophy,
  LinkSimple,
  WhatsappLogo,
  TelegramLogo,
  Bell,
  Image as ImageIcon,
  VideoCamera,
  MusicNote,
  Gift
} from "@phosphor-icons/react";
import CreatePostModal from "@/components/CreatePostModal";
import PostCard from "@/components/PostCard";

export default function FeedPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [sortBy, setSortBy] = useState("recent");
  const [mediaType, setMediaType] = useState("all");
  const [codeFilter, setCodeFilter] = useState("any");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const params = {};
      if (selectedTag) params.tag = selectedTag;
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (sortBy !== "smart") params.sort_by = sortBy;
      if (mediaType !== "all") params.media_type = mediaType;
      if (codeFilter === "with") params.has_code = true;
      if (codeFilter === "without") params.has_code = false;
      params.limit = 20;
      const response = await apiClient.get("/posts", { params });
      // Handle both old array response and new paginated response
      const data = response.data;
      setPosts(Array.isArray(data) ? data : (data.posts || []));
    } catch (error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, [selectedTag, searchQuery, sortBy, mediaType, codeFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiClient.get("/notifications/unread-count");
      setUnreadCount(response.data?.unread_count || 0);
    } catch {
      // Ignore transient notification polling errors
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiClient.get("/notifications", { params: { limit: 20 } });
      setNotifications(response.data || []);
    } catch {
      toast.error("Failed to load notifications");
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(intervalId);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await apiClient.post(`/notifications/${notificationId}/read`);
      setNotifications((prev) => prev.map((item) => item.id === notificationId ? { ...item, read: true } : item));
      fetchUnreadCount();
    } catch {
      toast.error("Could not mark notification");
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await apiClient.post("/notifications/read-all");
      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
      setUnreadCount(0);
      toast.success("Notifications marked as read");
    } catch {
      toast.error("Could not update notifications");
    }
  };

  const formatNotificationTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
    setShowCreateModal(false);
  };

  const handleLikeToggle = async (postId, liked, likesCount) => {
    setPosts(posts.map(p => 
      p.id === postId 
        ? { ...p, liked_by_user: liked, likes_count: likesCount }
        : p
    ));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCopyPublicLeaderboardLink = async () => {
    const link = `${window.location.origin}/leaderboard/public`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Public leaderboard link copied");
    } catch (error) {
      toast.error("Could not copy link");
    }
  };

  const handleSharePublicLeaderboardWhatsApp = () => {
    const link = `${window.location.origin}/leaderboard/public`;
    const text = `Check DIIT's public leaderboard: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleSharePublicLeaderboardTelegram = () => {
    const link = `${window.location.origin}/leaderboard/public`;
    const text = "Check DIIT's public leaderboard";
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  const allTags = [...new Set(posts.flatMap(p => p.tags))].slice(0, 10);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3 py-3">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
              <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
                <Code weight="bold" className="w-5 h-5 text-primary" />
              </div>
              <span className="font-heading font-bold text-lg hidden sm:block">DIIT Showcase</span>
            </Link>

            {/* Search */}
            <div className="hidden md:block flex-1 max-w-md mx-2 lg:mx-4">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search posts, tags, students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 bg-surface border border-border rounded-full text-sm focus:outline-none focus:border-primary transition-colors"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowNotifications((prev) => !prev)}
                  className="h-10 w-10 rounded-full"
                  data-testid="notifications-button"
                  title="Notifications"
                >
                  <Bell className="w-4 h-4" />
                </Button>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] flex items-center justify-center">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 max-h-[26rem] overflow-hidden rounded-xl border border-border bg-surface shadow-2xl z-50">
                    <div className="p-3 border-b border-border/40 flex items-center justify-between">
                      <p className="font-semibold">Notifications</p>
                      <button
                        type="button"
                        onClick={handleMarkAllNotificationsRead}
                        className="text-xs text-primary hover:text-primary/80"
                      >
                        Mark all read
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground">No notifications yet</div>
                      ) : (
                        notifications.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleMarkNotificationRead(item.id)}
                            className={`w-full text-left p-3 border-b border-border/30 last:border-0 hover:bg-background/40 transition-colors ${item.read ? "opacity-70" : ""}`}
                          >
                            <p className="text-sm font-medium">{item.title}</p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.message}</p>
                            <p className="text-[11px] text-muted-foreground mt-2">{formatNotificationTime(item.created_at)}</p>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleSharePublicLeaderboardWhatsApp}
                className="h-10 w-10 rounded-full hidden md:flex"
                data-testid="share-public-leaderboard-whatsapp"
                title="Share public leaderboard on WhatsApp"
              >
                <WhatsappLogo className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleSharePublicLeaderboardTelegram}
                className="h-10 w-10 rounded-full hidden md:flex"
                data-testid="share-public-leaderboard-telegram"
                title="Share public leaderboard on Telegram"
              >
                <TelegramLogo className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopyPublicLeaderboardLink}
                className="h-10 w-10 rounded-full"
                data-testid="copy-public-leaderboard-link"
                title="Copy public leaderboard link"
              >
                <LinkSimple className="w-4 h-4" />
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-10 px-4 rounded-full hidden md:flex items-center gap-2"
                data-testid="header-public-leaderboard-link"
              >
                <Link to="/leaderboard/public">
                  <Trophy className="w-4 h-4" />
                  <span>Public Board</span>
                </Link>
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium hidden sm:flex items-center gap-2"
                data-testid="create-post-button"
              >
                <Plus className="w-5 h-5" />
                <span>New Post</span>
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                size="icon"
                className="h-10 w-10 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full sm:hidden"
                data-testid="create-post-button-mobile"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="md:hidden mb-4">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search posts, tags, students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-surface border border-border rounded-full text-sm focus:outline-none focus:border-primary transition-colors"
              data-testid="search-input-mobile"
            />
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* User Card */}
              <div className="bg-surface rounded-xl p-4 border border-border/40">
                <Link 
                  to={`/profile/${user.id}`} 
                  className="flex items-center gap-3 group"
                  data-testid="user-profile-link"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-lg font-bold text-background">
                    {user.full_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate group-hover:text-primary transition-colors">
                      {user.full_name}
                    </p>
                    <p className="text-sm text-muted-foreground capitalize">{user.role}</p>
                  </div>
                </Link>
              </div>

              {/* Navigation */}
              <nav className="bg-surface rounded-xl border border-border/40 overflow-hidden">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-4 py-3 text-primary bg-primary/10 border-l-2 border-primary"
                  data-testid="nav-home"
                >
                  <House weight="fill" className="w-5 h-5" />
                  <span className="font-medium">Home Feed</span>
                </Link>
                <Link
                  to={`/profile/${user.id}`}
                  className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors"
                  data-testid="nav-profile"
                >
                  <User className="w-5 h-5" />
                  <span>My Profile</span>
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors"
                  data-testid="nav-settings"
                >
                  <GearSix className="w-5 h-5" />
                  <span>Settings</span>
                </Link>
                <Link
                  to="/referrals"
                  className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors"
                  data-testid="nav-referrals"
                >
                  <Gift className="w-5 h-5" />
                  <span>Referrals</span>
                </Link>
                <Link
                  to="/leaderboard/public"
                  className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors"
                  data-testid="nav-public-leaderboard"
                >
                  <Trophy className="w-5 h-5" />
                  <span>Public Leaderboard</span>
                </Link>
                {["admin", "teacher"].includes(user.role) && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition-colors"
                    data-testid="nav-admin"
                  >
                    <ShieldCheck className="w-5 h-5" />
                    <span>Admin Panel</span>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-danger transition-colors"
                  data-testid="logout-button"
                >
                  <SignOut className="w-5 h-5" />
                  <span>Log out</span>
                </button>
              </nav>

              {/* Tags */}
              {allTags.length > 0 && (
                <div className="bg-surface rounded-xl p-4 border border-border/40">
                  <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                    Popular Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {allTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          selectedTag === tag
                            ? "bg-primary text-primary-foreground"
                            : "bg-surface-elevated text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`tag-filter-${tag}`}
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Main Feed */}
          <main className="flex-1 min-w-0">
            {/* Mobile nav */}
            <div className="lg:hidden flex items-center gap-2 mb-4 overflow-x-auto pb-2">
              <Link
                to={`/profile/${user.id}`}
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <User className="w-4 h-4" />
                Profile
              </Link>
              <Link
                to="/referrals"
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <Gift className="w-4 h-4" />
                Referrals
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <GearSix className="w-4 h-4" />
                Settings
              </Link>
              <Link
                to="/leaderboard/public"
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <Trophy className="w-4 h-4" />
                Public Board
              </Link>
              <button
                type="button"
                onClick={handleCopyPublicLeaderboardLink}
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <LinkSimple className="w-4 h-4" />
                Copy Public Link
              </button>
              <button
                type="button"
                onClick={handleSharePublicLeaderboardWhatsApp}
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <WhatsappLogo className="w-4 h-4" />
                WhatsApp
              </button>
              <button
                type="button"
                onClick={handleSharePublicLeaderboardTelegram}
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
              >
                <TelegramLogo className="w-4 h-4" />
                Telegram
              </button>
              {["admin", "teacher"].includes(user.role) && (
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Admin
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-surface rounded-full text-sm whitespace-nowrap text-danger"
              >
                <SignOut className="w-4 h-4" />
                Logout
              </button>
            </div>

            {selectedTag && (
              <div className="mb-4 flex items-center gap-2 max-w-3xl mx-auto">
                <span className="text-muted-foreground">Filtering by:</span>
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                  #{selectedTag}
                </span>
                <button
                  onClick={() => setSelectedTag(null)}
                  className="text-muted-foreground hover:text-foreground text-sm"
                >
                  Clear
                </button>
              </div>
            )}

            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="h-9 px-3 rounded-full bg-surface border border-border text-sm"
                data-testid="feed-sort-filter"
              >
                <option value="recent">Recent</option>
                <option value="popular">Most Liked</option>
                <option value="comments">Most Commented</option>
              </select>
              <select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value)}
                className="h-9 px-3 rounded-full bg-surface border border-border text-sm"
                data-testid="feed-media-filter"
              >
                <option value="all">All Media</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
                <option value="audio">Audio</option>
              </select>
              <select
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value)}
                className="h-9 px-3 rounded-full bg-surface border border-border text-sm"
                data-testid="feed-code-filter"
              >
                <option value="any">Any Code</option>
                <option value="with">With Code Preview</option>
                <option value="without">Without Code</option>
              </select>
            </div>

            {loading ? (
              <div className="space-y-4 max-w-3xl mx-auto">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-surface rounded-xl p-6 animate-pulse">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-surface-elevated" />
                      <div className="space-y-2">
                        <div className="w-32 h-4 bg-surface-elevated rounded" />
                        <div className="w-20 h-3 bg-surface-elevated rounded" />
                      </div>
                    </div>
                      <div className="w-full h-56 bg-surface-elevated rounded-lg mb-4" />
                    <div className="space-y-2">
                      <div className="w-3/4 h-4 bg-surface-elevated rounded" />
                      <div className="w-1/2 h-4 bg-surface-elevated rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16 max-w-3xl mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface flex items-center justify-center">
                  <Code className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">No posts yet</h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery || selectedTag || mediaType !== "all" || codeFilter !== "any"
                    ? "No posts match these filters"
                    : "Be the first to share your work!"}
                </p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Post
                </Button>
              </div>
            ) : (
              <div className="space-y-4 stagger-children max-w-3xl mx-auto">
                {posts.map(post => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    onLikeToggle={handleLikeToggle}
                  />
                ))}
              </div>
            )}
          </main>

          {/* Right Sidebar - Stats */}
          <aside className="hidden xl:block w-72 shrink-0">
            <div className="sticky top-24 space-y-6">
              <div className="bg-surface rounded-xl p-5 border border-border/40">
                <h3 className="font-heading font-semibold mb-4">About DIIT Showcase</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  A community platform for DIIT Computers students to showcase their projects, 
                  share code demos, and inspire each other.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <ImageIcon className="w-4 h-4 text-primary" />
                    <span>Share images of your work</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <VideoCamera className="w-4 h-4 text-accent" />
                    <span>Upload video walkthroughs</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Code className="w-4 h-4 text-danger" />
                    <span>Live HTML/CSS/JS previews</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onPostCreated={handlePostCreated}
      />
    </div>
  );
}
